import { useUnmountedRef } from 'ahooks';
import { isAxiosError } from 'axios';
import { isEmpty, isNil, isUndefined } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { withAuthRetry } from 'src/axios/withAuthRetry';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { VITE_PAST_DUE_LADDER_FEATURE } from 'src/constants/envs';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { useSubscriptionManagerContext } from '../../pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import {
  tCancelForm,
  tPlan,
} from '../../pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import { SubscriptionManagerService } from '../../pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import { tBillingQuotaUsageUpdatedEvent } from '../SocketsProvider/Sockets.types';
import { eAlertType } from '../ToastProvider/Toast.enum';
import { useToastContext } from '../ToastProvider/Toast.hook';
import { useUserContext } from '../UserProvider/User.context';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context';
import { useAnalyticsAuth } from '../../hooks/analytics/auth/useAnalyticsAuth.hook';
import {
  INITIAL_SUBSCRIPTION_VALUES,
  LEGACY_PAST_DUE_MODAL_DAYS,
} from './Subscription.constants';
import {
  eBillingNotificationType,
  ePeriod,
  ePlanCode,
  ePlanFrequency,
  eSubscriptionStatus,
} from './Subscription.enums';
import SubscriptionService from './Subscription.service';
import {
  tSubscriptionContext,
  tSubscriptionSummary,
  tTrialConfirmationRequest,
} from './Subscription.types';
import {
  buildPlanTiersFromSettled,
  shouldShowTrialConfirmationModal,
} from './Subscription.utils';

function useSubscription(): tSubscriptionContext {
  const { addAlert } = useToastContext();
  const { currentWorkspace, workspaceInfo, isLoadingWorkspaceInfo } =
    useWorkspaceContext();
  const { currentUser, hasSession } = useUserContext();
  const { paymentMethods } = useSubscriptionManagerContext();
  const { registerWorkspaceProperties } = useAnalyticsAuth();
  const { t } = useTranslation('subscription/index');
  const navigate = useNavigate();
  const location = useLocation();

  const [subscription, setSubscription] = useState<tSubscriptionSummary>(
    INITIAL_SUBSCRIPTION_VALUES,
  );
  const [isSetUpAvailable, setIsSetUpAvailable] = useState<
    boolean | undefined
  >();
  const [shouldSeeConfirmationModal, setShouldSeeConfirmationModal] =
    useState<boolean>(false);
  /**
   * Si el resumen de la suscripción YA LLEGÓ para el workspace en curso.
   *
   * Existe porque el estado inicial de `subscription` son valores INVENTADOS
   * —`INACTIVE` con `pendingCancellation: false`— y eso hace que «todavía no
   * sé» y «no tiene cancelación agendada» sean el MISMO valor. Cualquier
   * consumidor que ramifique sobre esa distinción falla ABIERTO mientras no
   * haya datos: el flujo de recarga le ofrecía a un cliente con la baja
   * agendada configurar una recarga automática que nunca se iba a cobrar, sin
   * aviso y sin momento de retención (RET-1908).
   *
   * Y NO alcanza con `isLoadingSubscription`, que era el arreglo obvio: ese se
   * apaga en el `finally` incluso cuando la petición FALLA, así que tras un
   * error queda apagado con el estado en los valores por defecto — otra vez
   * indistinguible de «no tiene cancelación». Este se enciende solo donde
   * llegan datos de verdad.
   *
   * ADITIVO A PROPÓSITO. `subscription` no cambia de forma: 80 archivos leen
   * ese objeto y 59 sitios ramifican sobre sus campos. Volverlo nullable es el
   * arreglo de fondo y es una migración, no un fix. Esto le da al provider la
   * capacidad de DECIR que no sabe, y cada consumidor la adopta cuando le
   * toque.
   */
  const [subscriptionLoadedForId, setSubscriptionLoadedForId] = useState<string>();
  const hasSubscriptionData = !!currentWorkspace?.id &&
    subscriptionLoadedForId === currentWorkspace.id;
  const [isLoadingSubscription, setIsLoadingSubscription] =
    useState<boolean>(true);
  const [plans, setPlans] = useState<Record<ePlanFrequency, tPlan[]> | null>(
    null,
  );
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);

  const checkSetupAvailability = useCallback(async (wid: string) => {
    try {
      const availability = await SubscriptionService.getSetupAvailability(wid);
      setIsSetUpAvailable(availability.offer_available);
    } catch (e) {
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'Subscription.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al verificar disponibilidad de setup',
        e as unknown as Error,
      );
      setIsSetUpAvailable(true);
    }
  }, []);

  const checkSetupAvailabilityRef = useRef(checkSetupAvailability);

  // RET-1068: tracks the active workspace id so getSummary can detect
  // workspace switches mid-fetch and drop stale responses. Without this, a
  // pending Subscription.getSummary() call from a prior workspace can
  // resolve AFTER the new workspace's render and overwrite state (and
  // therefore the PostHog `workspace_plan` super property) with the wrong
  // plan, biasing every downstream event until the new fetch completes.
  const currentWorkspaceIdRef = useRef<string | undefined>(
    currentWorkspace?.id,
  );
  // Parity with Integrations.hook: a logout mid-retry must not fire the
  // post-backoff request against an unmounted tree (BUG-868 review nit).
  const unmountedRef = useUnmountedRef();
  useEffect(() => {
    currentWorkspaceIdRef.current = currentWorkspace?.id;
    /*
      Al cambiar de workspace se vuelve a "no sé": lo que se sabía del anterior
      no dice nada del nuevo, y dejarlo encendido durante el fetch entrante haría
      pasar el estado del workspace viejo por respuesta del nuevo — la misma
      clase de fuga que la guardia de RET-1068 evita para el plan.
    */
    setSubscriptionLoadedForId(undefined);
  }, [currentWorkspace?.id]);

  const currentNotifications = useMemo(
    () => subscription.notifications?.filter(
      (notification) => notification.type === eBillingNotificationType.PAST_DUE && !notification.isRead,
    ) ?? [],
    [subscription.notifications],
  );

  const filterTrialConfirmationNotifications =
    subscription?.notifications?.filter(
      (notification) =>
        notification.type === eBillingNotificationType.TRIAL_CONFIRMATION &&
        !notification.isRead,
    );

  const isClient =
    subscription.status === eSubscriptionStatus.ACTIVE ||
    subscription.status === eSubscriptionStatus.TRIAL ||
    subscription.status === eSubscriptionStatus.PAST_DUE ||
    subscription.status === eSubscriptionStatus.SCHEDULED_CANCELLATION;

  // BUG-868: "no sé" ≠ "no es cliente". Sin datos, `subscription` está en sus
  // defaults (INACTIVE) e `isClient` sale false aunque el workspace esté
  // pagado. Los paywalls gatean sobre esta señal para fallar ABIERTO: solo se
  // muestran cuando un summary real confirmó que no es cliente.
  const isKnownNonClient = hasSubscriptionData && !isClient;

  // AGI-427: "confirmed non-client" for UPSELL surfaces. `isKnownNonClient`
  // alone misses the main audience: a workspace that never paid has
  // `workspaceInfo.isActive === false`, and for those `getSummary()` never
  // runs (see the effect below), so `hasSubscriptionData` stays false forever.
  // An inactive workspace is a non-client by definition, so it counts too.
  // Still false before `workspaceInfo` loads — that is what stops the
  // upsell from flashing over paying workspaces during boot.
  const isConfirmedNonClient =
    isKnownNonClient ||
    (!isUndefined(workspaceInfo) && workspaceInfo.isActive === false);

  const basicMonthlyPlan = plans?.[ePlanFrequency.MONTHLY]?.find(
    (plan) => plan.planCode === 'PLAN_BASIC',
  );

  const setUpInfo = useMemo<tPlan | undefined>(
    () => basicMonthlyPlan ? ({
      ...basicMonthlyPlan,
      trialCost: basicMonthlyPlan.trialCost / 100,
      monthlyPrice: basicMonthlyPlan.totalPrice / 100,
    }) : undefined,
    [basicMonthlyPlan],
  );

  // AGI-435: gate on the fetch, not just the value. `workspaceInfo` is seeded
  // from the localStorage blob, which still reads inactive right after paying,
  // so redirecting on it alone threw paying customers into the plan picker.
  useEffect(() => {
    if (
      hasSession &&
      !isLoadingWorkspaceInfo &&
      !isUndefined(isSetUpAvailable) &&
      !isSetUpAvailable &&
      !isUndefined(workspaceInfo?.isActive) &&
      !workspaceInfo?.isActive &&
      !location.pathname.includes(RouterPaths.settings)
    ) {
      setIsLoadingSubscription(false);

      navigate(`${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}`, {
        replace: true,
      });

      addAlert({
        type: eAlertType.INFO,
        message: t('errors.workspaceNotActive'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [
    location.pathname,
    subscription.status,
    isSetUpAvailable,
    workspaceInfo?.isActive,
    isLoadingWorkspaceInfo,
    hasSession,
  ]);

  useEffect(() => {
    if (currentWorkspace?.id && workspaceInfo?.isActive) {
      getSummary();
    } else {
      setIsLoadingSubscription(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [currentWorkspace?.id, workspaceInfo?.isActive]);

  // A pause can start or end while an already-open tab is in the background.
  // Reconcile on focus whenever a live pause projection exists; this is also
  // what restores the composer after the pause window ends without a reload.
  useEffect(() => {
    if (!subscription.planPause || !currentWorkspace?.id) return undefined;
    const handleFocus = () => {
      void getSummary();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [subscription.planPause, currentWorkspace?.id]);

  // Refused-send surfaces publish this event after a confirmed paused code.
  // Dates and state still come from the summary; the refusal body is never
  // trusted as a second pause model.
  useEffect(() => {
    if (!currentWorkspace?.id) return undefined;
    const handlePausedRefusal = () => {
      void getSummary();
    };
    window.addEventListener(
      'leadsales:plan-pause-refusal',
      handlePausedRefusal,
    );
    return () =>
      window.removeEventListener(
        'leadsales:plan-pause-refusal',
        handlePausedRefusal,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (currentWorkspace?.id && workspaceInfo?.isActive) {
      LLog.info(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'Subscription.hook',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Filtrando notificaciones de prueba',
        { filterTrialConfirmationNotifications },
      );
      // RET-1506: single source of truth for the trial confirmation modal.
      // ACTIVE (already-paid) subscriptions never see it — even with a stale
      // unread notification — while PAUSED still forces it (trial expired,
      // no notification created). Defense-in-depth on top of the leadbilling
      // fix that marks the notification read on conversion.
      setShouldSeeConfirmationModal(
        shouldShowTrialConfirmationModal(
          subscription.status,
          filterTrialConfirmationNotifications?.length ?? 0,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [subscription]);

  useEffect(() => {
    if (
      currentWorkspace?.id === undefined ||
      workspaceInfo?.isActive === undefined
    ) {
      return;
    }
    checkSetupAvailabilityRef.current(currentWorkspace.id);
  }, [currentWorkspace?.id, workspaceInfo?.isActive]);

  useEffect(() => {
    if (!isClient && workspaceInfo && !workspaceInfo.isActive) {
      getPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [isClient, workspaceInfo]);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    registerWorkspaceProperties({
      workspace_plan: null,
      workspace_subscription_status: null,
    });
  }, [currentWorkspace?.id, registerWorkspaceProperties]);

  useEffect(() => {
    if (!hasSubscriptionData || !subscription.planCode) return;
    registerWorkspaceProperties({
      workspace_plan: subscription.planCode,
      workspace_subscription_status: subscription.status,
    });
  }, [hasSubscriptionData, subscription.planCode, subscription.status, registerWorkspaceProperties]);

  // Past Due and No Paid dynamic Modals

  // RET-1269 U8: las fronteras heredadas de los tres modales de mora, ya sin
  // lectura de entorno. Venían de `Number(import.meta.env.VITE_…) || 5` y de un
  // memo que armaba `[0, floor(n/2), n-1, n]`; con el 5 desplegado eso daba
  // `[0, 2, 4, 5]`, que es exactamente lo que quedó congelado en la constante.
  // El contador de abajo NO las consulta: sólo el resolver de modales, que vive
  // tras el flag y se apaga cuando la escalera se enciende.
  const [dayStart, dayMid, dayPrev, dayLast] = LEGACY_PAST_DUE_MODAL_DAYS;

  /**
   * Días de mora (RET-1269 U8).
   *
   * 🔴 Antes se derivaba de `endDate` —el fin del periodo facturado—, que NO es
   * el ancla de la mora. Los dos relojes cuentan desde puntos distintos, así
   * que un cliente que acababa de entrar en mora salía con un número NEGATIVO.
   * El arreglo es leer el `daysPastDue` que publica el BFF, no recortar el
   * negativo: `Math.max(0, …)` escondería el síntoma dejando las anclas
   * desalineadas.
   *
   * 🔴 Y ya no se apaga por `isEmpty(currentNotifications)`. Ese gate ataba el
   * contador a un estado sin relación —si al usuario no le había llegado
   * ninguna notificación de mora, la escalera entera se apagaba—, y se vuelve
   * fatal en cuanto U5 deje de escribir esas filas: nadie tendría contador.
   *
   * Lo que SÍ sigue siendo puerta es el estado `PAST_DUE`: `null` aquí
   * significa «este workspace no está en mora», nunca «cero días».
   */
  const daysPastDue = useMemo<number | null>(() => {
    if (subscription.status !== eSubscriptionStatus.PAST_DUE) {
      return null;
    }

    if (!VITE_PAST_DUE_LADDER_FEATURE) {
      // Camino heredado, intacto hasta que el flag se encienda.
      if (!subscription.endDate || isEmpty(currentNotifications)) {
        return null;
      }
      return moment().diff(moment(subscription.endDate), 'days');
    }

    // El valor publicado manda. `isNil` y no `||` ni `isEmpty`: `0` días de
    // mora es un valor REAL —el primer día— y ambos lo leerían como «no sé»,
    // tirando al cliente a la derivación heredada justo el día en que el ancla
    // nueva empieza a importar.
    if (!isNil(subscription.daysPastDue)) {
      return subscription.daysPastDue;
    }

    // Respaldo mientras el campo publicado termina de desplegarse; se retira en
    // un PR posterior, después de un release completo del backend.
    //
    // ⚠️ Lo que sale de aquí es SIEMPRE negativo para una suscripción morosa:
    // `endDate` espeja `expiration_date`, que Stripe adelanta un ciclo al EMITIR
    // la factura (producción midió de −17 a −363). Eso es deliberado —U8 se niega
    // a recortarlo con `Math.max(0, …)` porque el número negativo es justo la
    // evidencia de que las dos anclas discrepan, y U11 le conserva el signo en el
    // snapshot para que la discrepancia siga siendo visible.
    //
    // 🔴 Lo que NO puede pasar es que esa decisión deje al cliente sin aviso.
    // `pastDueDays` descarta todo negativo, así que este valor no puede elegir un
    // peldaño — y con la bandera encendida el resolver de modales ya está
    // apagado, así que «sin peldaño» era CERO señal (revisión adversarial de
    // Ivan, 2026-09-12). Lo resuelve `pastDueEarlyRung`, que ante un contador
    // inutilizable pinta el peldaño más suave en vez de callarse. El diagnóstico
    // se conserva aquí; la señal se garantiza allá.
    if (!subscription.endDate) {
      return null;
    }

    return moment().diff(moment(subscription.endDate), 'days');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U7: preserve the existing dependency list (KTD4).
  }, [subscription]);

  // Single if/else chain with exact matches:
  const modalType = useMemo<'first' | 'second' | 'third' | null>(() => {
    /**
     * 🔴 RET-1269: la escalera del Alert Center SUSTITUYE a esta cadena, y lo
     * que se apaga es el RESOLVER, no los componentes.
     *
     * La diferencia importa porque el retiro tiene que ser simétrico con el
     * flag. Borrar los modales fuera de él dejaba al cliente moroso sin NINGUNA
     * señal en la app: con el flag apagado la escalera devuelve el snapshot
     * vacío, las dos pantallas caen a la copia heredada y `isClient` sigue
     * contando PAST_DUE, así que nada bloquea ni redirige — el único aviso que
     * queda exige que el cliente navegue solo a Ajustes → Suscripción, justo en
     * los días que concentran el 76% de las recuperaciones (KTD10).
     *
     * Apagar el resolver deja exactamente una señal por estado del flag:
     *
     * - APAGADO → contador heredado derivado de `endDate` + estos tres modales,
     *   idéntico a `staging`. Se sostiene porque el re-anclaje de U8 también
     *   está tras el flag: con él apagado el contador de arriba sigue siendo el
     *   viejo, y estas fronteras siguen cayendo donde caían.
     * - ENCENDIDO → contador re-anclado + la escalera, y esta cadena oscura.
     *
     * Encenderlo NO puede dejar las dos a la vez: con el contador re-anclado la
     * comparación por igualdad de abajo se enciende en 0, 2, 4 y ≥5 y repite el
     * mismo modal día tras día sobre los avisos permanentes de la escalera.
     */
    if (VITE_PAST_DUE_LADDER_FEATURE) {
      return null;
    }

    if (!workspaceInfo?.isActive || daysPastDue === null) {
      return null;
    }

    // First modal: 3 days before end, only if no payment methods
    if (daysPastDue === -3 && paymentMethods.length === 0) {
      return 'first';
    }

    // Second modal: exact matches to dayStart, dayMid, dayPrev
    if (
      daysPastDue === dayStart ||
      daysPastDue === dayMid ||
      daysPastDue === dayPrev
    ) {
      return 'second';
    }

    // Third modal: dayLast or more
    if (daysPastDue >= dayLast) {
      return 'third';
    }

    return null;
  }, [
    workspaceInfo?.isActive,
    daysPastDue,
    dayStart,
    dayMid,
    dayPrev,
    dayLast,
    paymentMethods,
  ]);

  const getSummary = useCallback(async () => {
    // RET-1068: capture the workspace id at call-start and compare against
    // `currentWorkspaceIdRef` after every await. If the user switched
    // workspaces mid-flight, drop the writes — applying this response would
    // overwrite the new workspace's state (and PostHog super properties)
    // with the previous workspace's plan.
    const wsAtStart = currentWorkspace?.id;
    if (!wsAtStart) return;
    // BUG-868: the boot fan-out can 401 when Firebase hasn't settled yet. The
    // axios interceptor delegates that retry to the caller; without it this
    // catch left `subscription` on its INACTIVE default and every isClient
    // gate paywalled a paying customer.
    const abort = () =>
      currentWorkspaceIdRef.current !== wsAtStart || !!unmountedRef.current;
    const onRetry = ({ attempt }: { attempt: number }) =>
      LLog.warn(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'Subscription.hook',
          operation: LOG_OPERATIONS.API_CALL,
        },
        `Reintentando summary tras 401 degradado (intento ${attempt})`,
      );
    try {
      setIsLoadingSubscription(true);

      const summaryData = await withAuthRetry(
        () => SubscriptionService.getSummary(wsAtStart),
        { shouldAbort: abort, onRetry },
      );

      if (currentWorkspaceIdRef.current !== wsAtStart) return;

      if (isEmpty(summaryData)) {
        // Resetear a los defaults es volver a valores inventados: sin esto, el
        // reset se leería como "no tiene cancelación agendada".
        setSubscriptionLoadedForId(undefined);
        setSubscription(INITIAL_SUBSCRIPTION_VALUES);

        throw new Error(t('errors.subscriptionVerificationError'));
      }

      const features = await withAuthRetry(
        () => SubscriptionService.getFeatures(wsAtStart),
        { shouldAbort: abort, onRetry },
      );

      if (currentWorkspaceIdRef.current !== wsAtStart) return;

      // RET-1068: commit summary + features in a single setSubscription so
      // both writes are protected by the same staleness check above. The
      // previous two-step write left an incoherent window during the
      // getFeatures fetch — if the user switched workspaces mid-flight,
      // summary data from workspace A would land but the features update
      // would be dropped by the second guard, stranding subscription state
      // with A's plan/status until B's own getSummary completed.
      // Aquí, y solo aquí: es el único punto donde el resumen real del
      // workspace en curso se escribe (ya pasó la guardia de propiedad de
      // arriba, así que un fetch viejo no puede encenderlo).
      setSubscriptionLoadedForId(wsAtStart);
      setSubscription(() => ({
        ...summaryData,
        endDate: new Date(summaryData.endDate),
        startDate: summaryData.startDate
          ? new Date(summaryData.startDate)
          : new Date(),
        features: features.features,
        name: features.plan.name,
        planType: features.plan.planType,
        trialDays: features.plan.trialDays,
        outboundMessagingAccess:
          features.outboundMessagingAccess ??
          summaryData.outboundMessagingAccess ??
          true,
      }));

      if (
        summaryData.status === eSubscriptionStatus.TRIAL &&
        (summaryData.planCode === ePlanCode.PLAN_PROFESSIONAL ||
          summaryData.planCode === ePlanCode.PLAN_ENTERPRISE)
      ) {
        const basePlanCredits =
          (summaryData.planCode === ePlanCode.PLAN_PROFESSIONAL && 250) ||
          (summaryData.planCode === ePlanCode.PLAN_ENTERPRISE && 500) ||
          0;

        setSubscription((prev) => ({
          ...prev,
          planCredits: summaryData.templateCreditsPurchased
            ? summaryData.templateCreditsPurchased + basePlanCredits
            : basePlanCredits,
        }));
      }
    } catch (e) {
      // The service now rejects with the raw AxiosError (BUG-868); its
      // English "Request failed with status code 401" is not toast material,
      // and the old `error.message` on a bodyless rejection was a TypeError
      // inside this very catch. Crafted errors (empty-summary above) keep
      // their message.
      const error = e instanceof Error ? e : new Error(String(e));
      const message =
        e instanceof Error && !isAxiosError(e)
          ? e.message
          : t('errors.subscriptionVerificationError');

      addAlert({ type: eAlertType.ERROR, message });

      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'Subscription.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en SubscriptionProvider',
        error,
      );
    } finally {
      // RET-1068: only the fetch that still owns the active workspace may
      // clear the loading flag. A stale fetch's `finally` would otherwise
      // tear down the spinner while the newer fetch is still awaiting,
      // flashing UI gated on isLoadingSubscription.
      if (currentWorkspaceIdRef.current === wsAtStart) {
        setIsLoadingSubscription(false);
      }
    }
  }, [currentWorkspace?.id, unmountedRef, t, addAlert]);

  const markNotificationAsRead = useCallback(async () => {
    if (
      isEmpty(subscription.notifications) ||
      isEmpty(currentWorkspace?.id) ||
      isEmpty(currentNotifications)
    ) {
      return;
    }

    const pastDueNotification = currentNotifications[0];

    try {
      await SubscriptionService.readNotification(
        currentWorkspace?.id ?? '',
        pastDueNotification.id,
      );
    } catch (e) {
      const error = e as Error;

      addAlert({ type: eAlertType.ERROR, message: `${error.message}` });
    }

    getSummary();
  }, [subscription.notifications, currentWorkspace?.id, currentNotifications, addAlert, getSummary]);

  // RET-2428 (T-2, D-27): the card comes from the customer's choice in the
  // modal, never from the position of a card in the list.
  const sendTrialConfirmation = useCallback(async (request: tTrialConfirmationRequest): Promise<boolean> => {
    try {
      if (request.confirm === 'continue') {
        await SubscriptionService.confirmTrialSubscription(
          currentWorkspace?.id ?? '',
          request.paymentMethodId,
        );
      } else {
        await SubscriptionService.cancelTrialSubscription(
          currentWorkspace?.id ?? '',
          request.reason,
        );
      }
    } catch (e) {
      const error = e as Error;

      addAlert({ type: eAlertType.ERROR, message: `${error.message}` });
      return false;
    }

    await getSummary();
    return true;
  }, [currentWorkspace?.id, addAlert, getSummary]);


  /**
   * Rejects with the service's rejection when the cancellation was not
   * scheduled, so the caller can say so instead of announcing a success. The
   * summary is refreshed only after a confirmed schedule; `getSummary` reports
   * its own failures, so a refresh problem never turns the schedule into one.
   */
  const cancelCurrentSubscription = useCallback(async (questions: tCancelForm) => {
    // Map cancellationReason and description to the fields expected by the backend:
    // question1 gets the selected reason, question2 gets the description.
    await SubscriptionService.cancelSubscription(
      currentWorkspace?.id ?? '',
      questions.cancellationReason,
      questions.description,
      currentUser?.email ?? '',
    );
    await getSummary();
  }, [currentWorkspace?.id, currentUser?.email, getSummary]);

  const addAddon = useCallback(async (addonID: string, quantity: number) => {
    await SubscriptionManagerService.purchaseAddOn(
      String(currentWorkspace?.id),
      [{ uuid: addonID, quantity }],
    );
    await getSummary();
  }, [currentWorkspace?.id, getSummary]);

  const limitedActionAlert = useCallback(() => {
    addAlert({
      type: eAlertType.INFO,
      message:
        'Para poder usar esta funcionalidad debes tener una suscripción activa',
    });
  }, [addAlert]);

  // RET-1190: getPlans() can run concurrently (the workspaceInfo effect and the
  // retry button both invoke it). Without a guard, two in-flight runs race to
  // setPlans/addAlert — producing duplicate error toasts and letting a superseded
  // run overwrite fresher plans. Tag each run and only let the latest one commit.
  const getPlansRunRef = useRef(0);

  const getPlans = useCallback(async () => {
    const runId = ++getPlansRunRef.current;
    try {
      setLoadingPlans(true);

      // RET-1244: billing currency is resolved server-side by the BFF from the
      // CloudFront-Viewer-Country edge header (and the workspace's existing
      // subscription currency, when there is one). The browser no longer decides
      // it — the previous client-side ipapi.co / localStorage('country_code')
      // path mis-classified USD users as MXN. The returned plans carry their own
      // currency, which the UI renders.
      const plansResponse = await Promise.allSettled(
        Object.values(ePeriod).map(async (period) => {
          const plans = await SubscriptionService.getAvailablePlans(
            currentWorkspace?.id ?? '',
            period,
          );
          return plans;
        }),
      );

      // RET-1190: bail out if a newer getPlans() run has superseded this one, so
      // only the latest run owns the toast + plans state (no duplicate toasts or
      // stale overwrites).
      if (runId !== getPlansRunRef.current) {
        return;
      }

      // A rejected fetch (e.g. bff-web 502) used to be swallowed into an empty
      // plan set, leaving the upgrade modal silently blank. Surface an error toast
      // so the user can retry, while still showing whatever periods did load. A
      // genuinely empty (fulfilled) period is NOT an error.
      const { plans, hasFailedFetch } =
        buildPlanTiersFromSettled(plansResponse);
      if (hasFailedFetch) {
        addAlert({
          type: eAlertType.ERROR,
          message: t('errors.plansLoadFailed'),
        });
      }

      setPlans(plans);
    } catch (err) {
      if (runId === getPlansRunRef.current) {
        addAlert({ type: eAlertType.ERROR, message: `${err}` });
      }
    } finally {
      // Only the latest run clears the loading flag; an older run finishing late
      // must not flip it off while the newer run is still in flight.
      if (runId === getPlansRunRef.current) {
        setLoadingPlans(false);
      }
    }
  }, [currentWorkspace?.id, addAlert, t]);

  const handleFeatureUpdate = useCallback((event: tBillingQuotaUsageUpdatedEvent) => {
    setSubscription((prev) => {
      if (isEmpty(prev.features)) {
        return prev;
      }

      const updatedFeatures = prev.features.map((feature) => {
        if (feature.id === event.feature_usage.feature_id) {
          return { ...feature, usedCount: event.feature_usage.used_count };
        }
        return feature;
      });
      return {
        ...prev,
        features: updatedFeatures,
      };
    });
  }, []);

  return useMemo(
    () => ({
      subscription,
      getSummary,
      getPlans,
      markNotificationAsRead,
      shouldSeePrevPastDueModal: modalType === 'first',
      shouldSeePastDueModal: modalType === 'second',
      shouldSeeNoPaidModal: modalType === 'third',
      shouldSeeConfirmationModal,
      sendTrialConfirmation,
      isSetUpAvailable,
      cancelCurrentSubscription,
      addAddon,
      isClient,
      limitedActionAlert,
      setSubscription,
      setUpInfo,
      handleFeatureUpdate,
      hasSubscriptionData,
      isKnownNonClient,
      isConfirmedNonClient,
      isLoadingSubscription,
      setIsLoadingSubscription,
      plans,
      loadingPlans,
      daysPastDue,
    }),
    [
      subscription,
      getSummary,
      getPlans,
      markNotificationAsRead,
      modalType,
      shouldSeeConfirmationModal,
      sendTrialConfirmation,
      isSetUpAvailable,
      cancelCurrentSubscription,
      addAddon,
      isClient,
      limitedActionAlert,
      setSubscription,
      setUpInfo,
      handleFeatureUpdate,
      hasSubscriptionData,
      isKnownNonClient,
      isConfirmedNonClient,
      isLoadingSubscription,
      setIsLoadingSubscription,
      plans,
      loadingPlans,
      daysPastDue,
    ],
  );
}

export default useSubscription;
